<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import axios from 'axios';
import Auth from 'dashboard/api/auth';
import { useAccount } from 'dashboard/composables/useAccount';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['updateInbox']);

const store = useStore();
const { t } = useI18n();
const { accountId: currentAccountId } = useAccount();

// Create authenticated axios instance
const authAxios = axios.create();
if (Auth.hasAuthCookie()) {
  const authData = Auth.getAuthData();
  Object.assign(authAxios.defaults.headers.common, {
    'access-token': authData['access-token'],
    'token-type': authData['token-type'],
    client: authData.client,
    expiry: authData.expiry,
    uid: authData.uid,
  });
}

// ---------------------------------------------------------------------------
// Integration mode migration (legacy n8n -> direct QuePasa Channel).
// Endpoint lives in the channel-quepasa plugin; keeps inbox id + history.
// ---------------------------------------------------------------------------
const isMigratingToChannel = ref(false);

const migrateToChannel = async () => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      'Migrar esta caixa de entrada para o QuePasa Channel direto (sem n8n)? A caixa, conversas e histórico são mantidos; o webhook direto será registrado automaticamente.'
    )
  )
    return;

  isMigratingToChannel.value = true;
  try {
    const { data } = await authAxios.post(
      `/api/v1/accounts/${currentAccountId.value}/quepasa_channel/${props.inbox.id}/migrate_to_channel`
    );
    useAlert(data?.message || 'Migração concluída. Recarregando...');
    window.setTimeout(() => window.location.reload(), 1200);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Falha na migração');
    isMigratingToChannel.value = false;
  }
};

const additionalAttributes = computed(
  () =>
    props.inbox.additional_attributes ||
    props.inbox.channel?.additional_attributes ||
    {}
);

// Initialize with default values (will be updated by watch)
const socEnabled = ref(true); // Default true
const patEnabled = ref(true); // Default true
// notify_state = open_conversations_on_state_change
// Nullable boolean: null = system/default behavior
const notifyState = ref(null);
const typingEnabled = ref(false);
const groupsEnabled = ref(null); // null = not configured, false = disabled, true = enabled
const broadcastsEnabled = ref(null);
const readreceiptsEnabled = ref(null);
const readUpdateEnabled = ref(null);
const callsEnabled = ref(null);
const qrCodeImage = ref(null);
const loadingQRCode = ref(false);
const phoneNumber = ref(additionalAttributes.value.phone || '');
const DEFAULT_HISTORY_SYNC_DAYS = 'all';

const savedHistorySyncDays = ref(DEFAULT_HISTORY_SYNC_DAYS);
const historySyncDays = ref(DEFAULT_HISTORY_SYNC_DAYS);
const pairingCode = ref(null);
const loadingPairingCode = ref(false);
const savingHistorySync = ref(false);
const savingSoc = ref(false);
const savingPat = ref(false);
const savingTyping = ref(false);
const savingGroups = ref(false);
const savingBroadcasts = ref(false);
const savingReadreceipts = ref(false);
const savingReadUpdate = ref(false);
const savingCalls = ref(false);
const serverInfo = ref(null);
const healthStatus = ref({ state: 'N/A', raw: null });
const quepasaVersion = ref('N/A');
const loadingServerInfo = ref(false);
const serverInfoError = ref(null);
const serverInfoErrorStatus = ref(null); // Store HTTP status code
const quepasaStatusCode = ref(null); // Store original QuePasa status code
const showQRPanel = ref(false);
const showPairPanel = ref(false);
const controllingServer = ref(false);
const pairingCodePolling = ref(null); // Interval ID for status polling after pairing
const serverPrepared = ref(false);
const serverInfoRefreshTimeout = ref(null);
const rootElement = ref(null);
const legacyBannerObserver = ref(null);
const hiddenLegacyBannerDisplays = new Map();
const typingPolicy = ref({
  global_enabled: false,
  account_enabled: false,
  inbox_enabled: false,
  effective_enabled: false,
  can_configure_inbox: false,
});

const canStartServer = computed(() => {
  // Only show Start button when server is explicitly Stopped
  return healthStatus.value?.state === 'Stopped';
});

const canStopServer = computed(() => {
  // Only show Stop button when server is Ready (state_code > 2)
  return healthStatus.value?.state === 'Ready';
});

const canConfigureTyping = computed(
  () => typingPolicy.value?.can_configure_inbox === true
);

const typingPolicyReason = computed(() => {
  if (typingPolicy.value?.global_enabled !== true) {
    return t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.GLOBAL_DISABLED');
  }

  if (typingPolicy.value?.account_enabled !== true) {
    return t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.ACCOUNT_DISABLED');
  }

  return t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.INBOX_ALLOWED');
});

const normalizeHistorySyncDaysValue = rawValue => {
  const value = String(rawValue ?? '')
    .trim()
    .toLowerCase();

  if (!value) {
    return '';
  }

  if (value === 'all') {
    return 'all';
  }

  if (/^\d+$/.test(value)) {
    return String(Number.parseInt(value, 10));
  }

  return null;
};

const normalizedHistorySyncDays = computed(() =>
  normalizeHistorySyncDaysValue(historySyncDays.value)
);

const historySyncDaysInvalid = computed(
  () => normalizedHistorySyncDays.value === null
);

const historySyncDaysDirty = computed(
  () => normalizedHistorySyncDays.value !== savedHistorySyncDays.value
);

const connectionStatus = computed(() => {
  if (!healthStatus.value?.state) return 'N/A';
  // Return the exact state from QuePasa without mapping
  return healthStatus.value.state;
});

const statusColorClass = computed(() => {
  const state = healthStatus.value?.state;

  if (state === 'Ready') return 'text-green-600';
  if (state === 'UnVerified' || state === 'Disconnected')
    return 'text-amber-600';
  if (state === 'N/A') return 'text-n-slate-9';

  return 'text-n-slate-11';
});

const isPreparedState = state =>
  Boolean(state) && !['N/A', 'UnPrepared'].includes(state);

const isInboxNotPreparedStatus = (
  status,
  originalStatus = quepasaStatusCode.value
) => (status === 404 && originalStatus === 204) || status === 204;

const isInboxNotPreparedError = computed(
  () =>
    Boolean(serverInfoError.value) &&
    isInboxNotPreparedStatus(
      serverInfoErrorStatus.value,
      quepasaStatusCode.value
    )
);

const hasBlockingServerError = computed(
  () => Boolean(serverInfoError.value) && !isInboxNotPreparedError.value
);

const clearServerError = () => {
  serverInfoError.value = null;
  serverInfoErrorStatus.value = null;
  quepasaStatusCode.value = null;
};

const legacyBannerLabels = [
  'Server Not Found in QuePasa',
  'Servidor Não Encontrado no QuePasa',
];

const legacyBannerActions = ['Initialize Server', 'Inicializar Servidor'];

const getLegacyServerBanners = () => {
  const section = rootElement.value?.closest('section');
  if (!section) {
    return [];
  }

  return [...section.querySelectorAll('div')].filter(node => {
    if (node === rootElement.value || rootElement.value?.contains(node)) {
      return false;
    }

    const text = node.textContent?.replace(/\s+/g, ' ').trim() || '';
    return (
      legacyBannerLabels.some(label => text.includes(label)) &&
      legacyBannerActions.some(action => text.includes(action))
    );
  });
};

const hideLegacyServerBanners = async () => {
  await nextTick();

  getLegacyServerBanners().forEach(node => {
    if (!hiddenLegacyBannerDisplays.has(node)) {
      hiddenLegacyBannerDisplays.set(node, node.style.display || '');
    }

    node.style.display = 'none';
  });
};

const restoreLegacyServerBanners = () => {
  hiddenLegacyBannerDisplays.forEach((display, node) => {
    node.style.display = display;
  });
  hiddenLegacyBannerDisplays.clear();
};

const startLegacyBannerObserver = () => {
  const section = rootElement.value?.closest('section');
  if (!section || typeof MutationObserver === 'undefined') {
    return;
  }

  legacyBannerObserver.value = new MutationObserver(() => {
    hideLegacyServerBanners();
  });

  legacyBannerObserver.value.observe(section, {
    childList: true,
    subtree: true,
  });
};

const markServerPrepared = () => {
  serverPrepared.value = true;

  if (isInboxNotPreparedError.value) {
    clearServerError();
  }

  hideLegacyServerBanners();
};

const syncPreparedServerState = state => {
  if (isPreparedState(state)) {
    markServerPrepared();
  }
};

const saveSocSettings = async () => {
  if (savingSoc.value) return;

  try {
    savingSoc.value = true;

    const payload = {
      channel: {
        additional_attributes: {
          ...additionalAttributes.value,
          soc: socEnabled.value,
        },
      },
    };

    const response = await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      ...payload,
    });

    emit('updateInbox', response);
    useAlert(t('QUEPASA_INBOX.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
  } finally {
    savingSoc.value = false;
  }
};

const savePatSettings = async () => {
  if (savingPat.value) return;

  try {
    savingPat.value = true;

    const payload = {
      channel: {
        additional_attributes: {
          ...additionalAttributes.value,
          pat: patEnabled.value,
        },
      },
    };

    const response = await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      ...payload,
    });

    emit('updateInbox', response);
    useAlert(t('QUEPASA_INBOX.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
  } finally {
    savingPat.value = false;
  }
};

const loadTypingPolicy = async () => {
  try {
    const result = await window.quepasa.service.server.typingPolicy(
      currentAccountId.value,
      props.inbox.id
    );
    typingPolicy.value = result;
  } catch (error) {
    typingPolicy.value = {
      global_enabled: false,
      account_enabled: false,
      inbox_enabled: false,
      effective_enabled: false,
      can_configure_inbox: false,
    };
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
  }
};

const saveTypingSettings = async () => {
  if (savingTyping.value || !canConfigureTyping.value) return;

  try {
    savingTyping.value = true;

    const payload = {
      channel: {
        additional_attributes: {
          ...additionalAttributes.value,
          typing_enabled: typingEnabled.value,
        },
      },
    };

    const response = await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      ...payload,
    });

    emit('updateInbox', response);
    await loadTypingPolicy();
    useAlert(t('QUEPASA_INBOX.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
  } finally {
    savingTyping.value = false;
  }
};

const saveNotifyState = async () => {
  try {
    const payload = {
      channel: {
        additional_attributes: {
          ...additionalAttributes.value,
          notify_state: notifyState.value,
        },
      },
    };

    const response = await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      ...payload,
    });

    emit('updateInbox', response);
    useAlert(t('QUEPASA_INBOX.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
  }
};

const persistHistorySyncDays = async ({ showSuccess = true } = {}) => {
  if (savingHistorySync.value) {
    return false;
  }

  if (historySyncDaysInvalid.value) {
    useAlert(t('QUEPASA_INBOX.SETTINGS.HISTORY_SYNC.INVALID'));
    return false;
  }

  if (!historySyncDaysDirty.value) {
    return true;
  }

  try {
    savingHistorySync.value = true;

    const nextAdditionalAttributes = {
      ...additionalAttributes.value,
    };

    if (normalizedHistorySyncDays.value) {
      nextAdditionalAttributes.historysyncdays =
        normalizedHistorySyncDays.value;
    } else {
      delete nextAdditionalAttributes.historysyncdays;
    }

    const response = await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        additional_attributes: nextAdditionalAttributes,
      },
    });

    savedHistorySyncDays.value = normalizedHistorySyncDays.value || '';
    emit('updateInbox', response);

    if (showSuccess) {
      useAlert(t('QUEPASA_INBOX.SETTINGS.SAVE_SUCCESS'));
    }

    return true;
  } catch (error) {
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
    return false;
  } finally {
    savingHistorySync.value = false;
  }
};

// Called when pairing succeeds — defined before startPairingStatusPolling to avoid forward-reference
// loadServerInfo is declared later; this closure captures it at call-time (not definition-time)
const onPairingConnected = async () => {
  // eslint-disable-next-line no-use-before-define
  await loadServerInfo();
  useAlert(t('QUEPASA_INBOX.SETTINGS.PAIRING_CODE.CONNECTED'));
};

const startPairingStatusPolling = () => {
  // Clear any existing polling
  if (pairingCodePolling.value) {
    clearInterval(pairingCodePolling.value);
  }

  const maxAttempts = 36; // 3 minutes at 5s intervals
  let attempts = 0;

  pairingCodePolling.value = setInterval(async () => {
    attempts += 1;
    try {
      const accountId = window.location.pathname.split('/')[3];
      const healthResponse = await authAxios.get(
        `/api/v1/accounts/${accountId}/quepasa/server/health`,
        { params: { inbox_id: props.inbox.id } }
      );

      const state = healthResponse.data?.state;

      // Update health status reactively so the UI reflects current state
      healthStatus.value = {
        state: state || 'N/A',
        raw: healthResponse.data,
      };
      syncPreparedServerState(state);

      if (state === 'Ready') {
        // Connected! Reload full server info and stop polling
        clearInterval(pairingCodePolling.value);
        pairingCodePolling.value = null;
        pairingCode.value = null;
        // Use onConnected callback to avoid circular forward-reference
        onPairingConnected();
      } else if (attempts >= maxAttempts) {
        // Timeout: stop polling, show a message
        clearInterval(pairingCodePolling.value);
        pairingCodePolling.value = null;
        useAlert(t('QUEPASA_INBOX.SETTINGS.PAIRING_CODE.TIMEOUT'));
      }
    } catch {
      // Ignore transient errors during polling
    }
  }, 5000); // Check every 5 seconds
};

const loadQRCode = async () => {
  try {
    const historySyncPersisted = await persistHistorySyncDays({
      showSuccess: false,
    });

    if (!historySyncPersisted) {
      return;
    }

    loadingQRCode.value = true;

    // Revoke previous blob URL
    if (qrCodeImage.value?.startsWith('blob:')) {
      URL.revokeObjectURL(qrCodeImage.value);
      qrCodeImage.value = null;
    }

    const accountId = window.location.pathname.split('/')[3];
    const qrResponse = await authAxios.get(
      `/api/v1/accounts/${accountId}/quepasa/scan`,
      {
        params: {
          inbox_id: props.inbox.id,
          ...(normalizedHistorySyncDays.value
            ? { historysyncdays: normalizedHistorySyncDays.value }
            : {}),
        },
        responseType: 'blob',
      }
    );
    const blob = qrResponse.data;
    qrCodeImage.value = URL.createObjectURL(blob);
    markServerPrepared();
    // Scan success means QuePasa already created the inbox server.
    // Reload the server info so the stale "not prepared" banner disappears.
    // eslint-disable-next-line no-use-before-define
    scheduleServerInfoRefresh();
  } catch (error) {
    // Error loading QR Code
  } finally {
    loadingQRCode.value = false;
  }
};

const refreshQRCode = () => {
  loadQRCode();
};

const requestPairingCode = async () => {
  if (!phoneNumber.value || loadingPairingCode.value) {
    return;
  }

  try {
    const historySyncPersisted = await persistHistorySyncDays({
      showSuccess: false,
    });

    if (!historySyncPersisted) {
      return;
    }

    loadingPairingCode.value = true;
    pairingCode.value = null;

    const accountId = window.location.pathname.split('/')[3];

    const response = await authAxios.post(
      `/api/v1/accounts/${accountId}/quepasa/pairing`,
      {
        inbox_id: props.inbox.id,
        phone: phoneNumber.value,
        ...(normalizedHistorySyncDays.value
          ? { historysyncdays: normalizedHistorySyncDays.value }
          : {}),
      }
    );

    pairingCode.value = response.data.code;
    markServerPrepared();

    // Save phone to additional_attributes
    const updateResponse = await authAxios.patch(
      `/api/v1/accounts/${accountId}/inboxes/${props.inbox.id}`,
      {
        channel: {
          additional_attributes: {
            ...additionalAttributes.value,
            phone: phoneNumber.value,
          },
        },
      }
    );
    emit('updateInbox', updateResponse.data);
    useAlert(
      t('QUEPASA_INBOX.SETTINGS.PAIRING_CODE.CODE_ALERT', {
        code: pairingCode.value,
      })
    );

    // Start polling for connection status — stops when Ready or after 3 minutes
    startPairingStatusPolling();
    // eslint-disable-next-line no-use-before-define
    scheduleServerInfoRefresh();
  } catch (error) {
    useAlert(
      error.message || t('QUEPASA_INBOX.SETTINGS.PAIRING_CODE.REQUEST_ERROR')
    );
  } finally {
    loadingPairingCode.value = false;
  }
};

const formatPhoneNumber = wid => {
  if (!wid) return '';

  // Extract number and section from WhatsApp ID format: "5521967609095:92@s.whatsapp.net"
  const match = wid.match(/^(\d+):(\d+)@/);
  if (!match) return wid;

  const number = match[1];

  // Format Brazilian phone number: +55 21 96760-9095 (without section)
  if (number.startsWith('55') && number.length >= 12) {
    const country = number.slice(0, 2); // 55
    const area = number.slice(2, 4); // 21
    const firstPart = number.slice(4, 9); // 96760
    const secondPart = number.slice(9, 13); // 9095

    return `+${country} ${area} ${firstPart}-${secondPart}`;
  }

  // Fallback: just add + prefix
  return `+${number}`;
};

const getConnectedNumber = () => {
  if (serverInfo.value?.wid) return formatPhoneNumber(serverInfo.value.wid);
  if (serverInfo.value?.phone) return formatPhoneNumber(serverInfo.value.phone);
  // Don't use 'user' field as it contains username, not phone number
  return 'N/A';
};

const getConnectedSection = () => {
  if (!serverInfo.value?.wid) return 'N/A';

  const match = serverInfo.value.wid.match(/^(\d+):(\d+)@/);
  return match ? match[2] : 'N/A';
};

const loadHealthStatus = async () => {
  try {
    const accountId = window.location.pathname.split('/')[3];
    const response = await authAxios.get(
      `/api/v1/accounts/${accountId}/quepasa/server/health`,
      {
        params: { inbox_id: props.inbox.id },
      }
    );

    // Use real QuePasa state directly (e.g., "Ready", "Disconnected", etc.)
    const state = response.data?.state || 'N/A';
    healthStatus.value = {
      state,
      raw: response.data,
    };
    syncPreparedServerState(state);

    // Keep the global reactive inboxStates in sync so the sidebar indicator updates
    if (window.quepasa?.inboxStates && state !== 'N/A') {
      window.quepasa.inboxStates[props.inbox.id] = state;
    }
  } catch (error) {
    // Health check failed, set to N/A
    healthStatus.value = { state: 'N/A', raw: null };
  }
};

const loadServerInfo = async () => {
  // Don't clear error states until we get a successful response
  // This prevents the flash of form fields appearing/disappearing
  loadingServerInfo.value = true;

  try {
    const accountId = window.location.pathname.split('/')[3];
    const response = await authAxios.get(
      `/api/v1/accounts/${accountId}/quepasa/server/info`,
      {
        params: { inbox_id: props.inbox.id },
      }
    );

    markServerPrepared();
    clearServerError();
    serverInfo.value = response.data.server;
    quepasaVersion.value = response.data.version || 'N/A';

    // Load feature statuses - preserve null for unconfigured
    const normalizeValue = val => {
      if (val === true) return true;
      if (val === false) return false;
      return null;
    };

    groupsEnabled.value = normalizeValue(response.data.server?.groups);
    broadcastsEnabled.value = normalizeValue(response.data.server?.broadcasts);
    readreceiptsEnabled.value = normalizeValue(
      response.data.server?.readreceipts
    );
    readUpdateEnabled.value = normalizeValue(response.data.server?.readupdate);
    callsEnabled.value = normalizeValue(response.data.server?.calls);

    // Auto-fill phone number from server info if not already set
    if (serverInfo.value?.wid && !phoneNumber.value) {
      phoneNumber.value = formatPhoneNumber(serverInfo.value.wid);
    }

    // Load health status
    await loadHealthStatus();
  } catch (error) {
    serverInfoErrorStatus.value = error.response?.status;
    quepasaStatusCode.value = error.response?.data?.quepasa_status;
    serverInfoError.value =
      error.response?.data?.error ||
      error.message ||
      'Failed to connect to QuePasa server';
  } finally {
    loadingServerInfo.value = false;
  }
};

const scheduleServerInfoRefresh = (delayMs = 750) => {
  if (serverInfoRefreshTimeout.value) {
    clearTimeout(serverInfoRefreshTimeout.value);
  }

  serverInfoRefreshTimeout.value = setTimeout(() => {
    serverInfoRefreshTimeout.value = null;
    loadServerInfo();
  }, delayMs);
};

const startServer = async () => {
  if (controllingServer.value) return;

  try {
    controllingServer.value = true;
    const accountId = window.location.pathname.split('/')[3];

    await authAxios.post(`/api/v1/accounts/${accountId}/quepasa/server/start`, {
      inbox_id: props.inbox.id,
    });

    markServerPrepared();
    useAlert(t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.START_SERVER_SUCCESS'));
    await loadServerInfo();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('QUEPASA_INBOX.ERRORS.CONNECTION_FAILED')
    );
  } finally {
    controllingServer.value = false;
  }
};

const stopServer = async () => {
  if (controllingServer.value) return;

  try {
    controllingServer.value = true;
    const accountId = window.location.pathname.split('/')[3];

    await authAxios.post(`/api/v1/accounts/${accountId}/quepasa/server/stop`, {
      inbox_id: props.inbox.id,
    });

    useAlert(t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.STOP_SERVER_SUCCESS'));
    await loadServerInfo();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('QUEPASA_INBOX.ERRORS.CONNECTION_FAILED')
    );
  } finally {
    controllingServer.value = false;
  }
};

const setFeatureSetting = async (featureName, value, loadingRef) => {
  if (loadingRef.value) return;

  try {
    loadingRef.value = true;
    const accountId = window.location.pathname.split('/')[3];

    const response = await authAxios.patch(
      `/api/v1/accounts/${accountId}/quepasa/server/info`,
      {
        inbox_id: props.inbox.id,
        [featureName]: value,
      }
    );

    if (response.data.success) {
      useAlert(t('QUEPASA_INBOX.SETTINGS.SAVE_SUCCESS'));
      await loadServerInfo();
    }
  } catch (error) {
    useAlert(error.message || t('QUEPASA_INBOX.SETTINGS.SAVE_ERROR'));
  } finally {
    loadingRef.value = false;
  }
};

const setGroups = value => setFeatureSetting('groups', value, savingGroups);
const setBroadcasts = value =>
  setFeatureSetting('broadcasts', value, savingBroadcasts);
const setReadreceipts = value =>
  setFeatureSetting('readreceipts', value, savingReadreceipts);
const setReadUpdate = value =>
  setFeatureSetting('readupdate', value, savingReadUpdate);
const setCalls = value => setFeatureSetting('calls', value, savingCalls);

const normalizeNullableBoolean = value => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const handleInboxStateChanged = event => {
  const payload = event?.detail || {};
  const inboxId = payload?.inbox_id;

  if (String(inboxId) !== String(props.inbox.id)) {
    return;
  }

  const state = payload?.current_state;
  if (state) {
    healthStatus.value = {
      state,
      raw: healthStatus.value?.raw || null,
    };
  }

  if (isPreparedState(state)) {
    markServerPrepared();
    scheduleServerInfoRefresh();
  }

  hideLegacyServerBanners();
};

// Watch for QR panel opening and load QR code if not already loaded
watch(showQRPanel, isOpen => {
  if (isOpen && !qrCodeImage.value) {
    loadQRCode();
  }
});

// Watch for inbox changes to update SOC and PAT values
watch(
  () => props.inbox,
  newInbox => {
    const attrs =
      newInbox?.additional_attributes ||
      newInbox?.channel?.additional_attributes;
    if (attrs) {
      const previousSavedHistorySync = savedHistorySyncDays.value;
      const normalizedSavedHistorySync =
        normalizeHistorySyncDaysValue(attrs.historysyncdays) || '';

      savedHistorySyncDays.value =
        normalizedSavedHistorySync || DEFAULT_HISTORY_SYNC_DAYS;
      if (
        historySyncDays.value === previousSavedHistorySync ||
        historySyncDays.value === '' ||
        historySyncDays.value === DEFAULT_HISTORY_SYNC_DAYS
      ) {
        historySyncDays.value =
          normalizedSavedHistorySync || DEFAULT_HISTORY_SYNC_DAYS;
      }

      socEnabled.value = attrs.soc !== false; // Default true
      patEnabled.value = attrs.pat !== false; // Default true
      typingEnabled.value = attrs.typing_enabled === true;
      notifyState.value = normalizeNullableBoolean(attrs.notify_state);
    }

    if (newInbox?.id) {
      loadTypingPolicy();
    }
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  window.addEventListener(
    'quepasa:inbox-state-changed',
    handleInboxStateChanged
  );
  hideLegacyServerBanners();
  startLegacyBannerObserver();
  loadTypingPolicy();
  loadServerInfo();
});

onBeforeUnmount(() => {
  window.removeEventListener(
    'quepasa:inbox-state-changed',
    handleInboxStateChanged
  );
  if (qrCodeImage.value?.startsWith('blob:')) {
    URL.revokeObjectURL(qrCodeImage.value);
  }
  if (pairingCodePolling.value) {
    clearInterval(pairingCodePolling.value);
  }
  if (serverInfoRefreshTimeout.value) {
    clearTimeout(serverInfoRefreshTimeout.value);
  }
  if (legacyBannerObserver.value) {
    legacyBannerObserver.value.disconnect();
    legacyBannerObserver.value = null;
  }
  restoreLegacyServerBanners();
});
</script>

<template>
  <div ref="rootElement" class="mx-8">
    <!-- Technical Error Message -->
    <div
      v-if="hasBlockingServerError"
      class="mt-4 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg"
    >
      <div class="flex items-start gap-3">
        <fluent-icon
          icon="error-circle"
          size="20"
          class="text-red-600 mt-0.5"
        />
        <div class="flex-1">
          <p class="text-sm font-medium text-red-800 mb-1">
            {{ $t('QUEPASA_INBOX.ERRORS.CONNECTION_FAILED') }}
          </p>
          <p class="text-xs text-red-600">
            {{ serverInfoError }}
          </p>
          <NextButton
            size="sm"
            color-scheme="secondary"
            class="mt-3"
            @click="loadServerInfo"
          >
            {{ $t('QUEPASA_INBOX.ERRORS.RETRY') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- WhatsApp Connection Section -->
    <SettingsSection
      v-if="!hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.CONNECTION.TITLE')"
      :sub-title="$t('QUEPASA_INBOX.SETTINGS.CONNECTION.DESCRIPTION')"
    >
      <div class="flex flex-col gap-4">
        <div class="rounded-lg border border-n-slate-6 bg-n-slate-1 p-4">
          <div class="flex flex-col gap-3">
            <div>
              <label
                for="history-sync-days"
                class="block text-sm font-medium text-n-slate-12"
              >
                {{ $t('QUEPASA_INBOX.SETTINGS.HISTORY_SYNC.LABEL') }}
              </label>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ $t('QUEPASA_INBOX.SETTINGS.HISTORY_SYNC.HELP_TEXT') }}
              </p>
            </div>

            <input
              id="history-sync-days"
              v-model="historySyncDays"
              type="text"
              :placeholder="
                $t('QUEPASA_INBOX.SETTINGS.HISTORY_SYNC.PLACEHOLDER')
              "
              class="px-3 py-2 text-sm border border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
            />

            <p v-if="historySyncDaysInvalid" class="text-xs text-red-600">
              {{ $t('QUEPASA_INBOX.SETTINGS.HISTORY_SYNC.INVALID') }}
            </p>

            <div class="flex justify-end">
              <NextButton
                size="sm"
                :label="$t('QUEPASA_INBOX.SETTINGS.SAVE')"
                :is-loading="savingHistorySync"
                :disabled="historySyncDaysInvalid || !historySyncDaysDirty"
                @click="persistHistorySyncDays()"
              />
            </div>
          </div>
        </div>

        <!-- QR Code Accordion -->
        <div class="border border-n-slate-6 rounded-lg overflow-hidden">
          <button
            type="button"
            class="flex items-center justify-between w-full p-4 text-left hover:bg-n-slate-2 transition-colors"
            @click="showQRPanel = !showQRPanel"
          >
            <div class="flex flex-col gap-1">
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('QUEPASA_INBOX.INBOX.QR_CODE.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ $t('QUEPASA_INBOX.INBOX.QR_CODE.METHOD_DESC') }}
              </p>
            </div>
            <fluent-icon
              :icon="showQRPanel ? 'chevron-up' : 'chevron-down'"
              size="20"
              class="text-n-slate-11"
            />
          </button>

          <div
            v-if="showQRPanel"
            class="p-4 border-t border-n-slate-6 bg-n-slate-1"
          >
            <div class="flex flex-col items-center gap-3">
              <p class="text-sm text-center text-n-slate-11 mb-3">
                {{ $t('QUEPASA_INBOX.SETTINGS.QR_CODE_INSTRUCTIONS') }}
              </p>
              <div
                class="w-48 h-48 rounded-lg border border-n-slate-6 overflow-hidden flex items-center justify-center"
                :class="qrCodeImage ? 'bg-white' : 'bg-n-slate-2 animate-pulse'"
              >
                <img
                  v-if="qrCodeImage"
                  :src="qrCodeImage"
                  alt="QR Code"
                  class="w-full h-full object-contain p-2"
                />
                <span v-else class="text-n-slate-9 text-xs">
                  {{ $t('QUEPASA_INBOX.SETTINGS.QR_CODE.LOADING') }}
                </span>
              </div>
              <NextButton
                icon="i-lucide-refresh-cw"
                :label="$t('QUEPASA_INBOX.SETTINGS.REFRESH_QR_CODE')"
                :is-loading="loadingQRCode"
                @click="refreshQRCode"
              />
            </div>
          </div>
        </div>

        <!-- Pairing Code Accordion -->
        <div class="border border-n-slate-6 rounded-lg overflow-hidden">
          <button
            type="button"
            class="flex items-center justify-between w-full p-4 text-left hover:bg-n-slate-2 transition-colors"
            @click="showPairPanel = !showPairPanel"
          >
            <div class="flex flex-col gap-1">
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('QUEPASA_INBOX.INBOX.PAIRING_CODE.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ $t('QUEPASA_INBOX.INBOX.PAIRING_CODE.METHOD_DESC') }}
              </p>
            </div>
            <fluent-icon
              :icon="showPairPanel ? 'chevron-up' : 'chevron-down'"
              size="20"
              class="text-n-slate-11"
            />
          </button>

          <div
            v-if="showPairPanel"
            class="p-4 border-t border-n-slate-6 bg-n-slate-1"
          >
            <div class="flex flex-col gap-4">
              <p class="text-xs text-n-slate-11">
                {{ $t('QUEPASA_INBOX.SETTINGS.PAIRING_CODE.DESCRIPTION') }}
              </p>
              <input
                v-model="phoneNumber"
                type="text"
                :placeholder="
                  $t('QUEPASA_INBOX.SETTINGS.PHONE_NUMBER_PLACEHOLDER')
                "
                class="px-3 py-2 text-sm border border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
                @keydown.enter.prevent="requestPairingCode"
              />
              <NextButton
                :label="$t('QUEPASA_INBOX.SETTINGS.GENERATE_CODE')"
                :is-loading="loadingPairingCode"
                :disabled="!phoneNumber"
                @click="requestPairingCode"
              />
              <div v-if="pairingCode" class="flex flex-col items-center gap-3">
                <div
                  class="text-center text-3xl font-mono font-bold text-n-brand py-4 px-6 bg-n-brand/10 rounded-lg border border-n-brand/30 w-full"
                >
                  {{ pairingCode }}
                </div>
                <div
                  v-if="pairingCodePolling"
                  class="flex items-center gap-2 text-xs text-n-slate-11"
                >
                  <span
                    class="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                  />
                  {{
                    $t(
                      'QUEPASA_INBOX.SETTINGS.PAIRING_CODE.WAITING_FOR_CONNECTION'
                    )
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>

    <!-- Server Info Section -->
    <SettingsSection
      v-if="serverInfo && !hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.TITLE')"
      :sub-title="$t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.DESCRIPTION')"
    >
      <div class="flex flex-col gap-4">
        <!-- Basic Info Grid - 3 columns -->
        <div class="grid grid-cols-3 gap-6">
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.CONNECTED_NUMBER') }}
            </label>
            <p class="text-sm font-medium text-n-slate-12">
              {{ getConnectedNumber() }}
            </p>
          </div>
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.SECTION') }}
            </label>
            <p class="text-sm font-medium text-n-slate-12">
              {{ getConnectedSection() }}
            </p>
          </div>
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.STATUS') }}
            </label>
            <p class="text-sm font-medium" :class="statusColorClass">
              {{ connectionStatus }}
            </p>
          </div>
        </div>

        <!-- Health Details Grid - 3 columns with button -->
        <div
          v-if="healthStatus.raw"
          class="grid grid-cols-3 gap-6 pt-4 border-t border-n-slate-6 items-end"
        >
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.QUEPASA_VERSION') }}
            </label>
            <p class="text-sm font-medium text-n-slate-12">
              {{
                quepasaVersion ||
                $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.NOT_AVAILABLE')
              }}
            </p>
          </div>
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.LAST_CHECK') }}
            </label>
            <p class="text-sm font-medium text-n-slate-12">
              {{
                healthStatus.raw?.timestamp
                  ? new Date(healthStatus.raw.timestamp).toLocaleString('pt-BR')
                  : $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.NOT_AVAILABLE')
              }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <NextButton
              v-if="canStartServer"
              icon="i-lucide-play"
              color-scheme="success"
              size="sm"
              :is-loading="controllingServer"
              @click="startServer"
            >
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.START_SERVER') }}
            </NextButton>
            <NextButton
              v-if="canStopServer"
              icon="i-lucide-circle-x"
              color-scheme="alert"
              size="sm"
              :is-loading="controllingServer"
              @click="stopServer"
            >
              {{ $t('QUEPASA_INBOX.SETTINGS.SERVER_INFO.STOP_SERVER') }}
            </NextButton>
          </div>
        </div>
      </div>
    </SettingsSection>

    <!-- SOC Toggle Section -->
    <SettingsSection :title="$t('QUEPASA_INBOX.SETTINGS.SOC.LABEL')">
      <div class="flex flex-col gap-2">
        <Switch v-model="socEnabled" @change="saveSocSettings" />
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.SOC.HELP_TEXT') }}
        </p>
        <NextButton
          v-if="savingSoc"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- PAT Mode Toggle Section -->
    <SettingsSection :title="$t('QUEPASA_INBOX.SETTINGS.PAT.LABEL')">
      <div class="flex flex-col gap-2">
        <Switch v-model="patEnabled" @change="savePatSettings" />
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.PAT.HELP_TEXT') }}
        </p>
        <NextButton
          v-if="savingPat"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <SettingsSection :title="$t('QUEPASA_INBOX.SETTINGS.TYPING.LABEL')">
      <div class="flex flex-col gap-2">
        <Switch
          v-model="typingEnabled"
          :disabled="savingTyping || !canConfigureTyping"
          @change="saveTypingSettings"
        />
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.TYPING.HELP_TEXT') }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{ typingPolicyReason }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{
            $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.GLOBAL_STATUS', {
              status: typingPolicy.global_enabled
                ? $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.ENABLED')
                : $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.DISABLED'),
            })
          }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{
            $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.ACCOUNT_STATUS', {
              status: typingPolicy.account_enabled
                ? $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.ENABLED')
                : $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.DISABLED'),
            })
          }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{
            $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.INBOX_STATUS', {
              status: typingEnabled
                ? $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.ENABLED')
                : $t('QUEPASA_INBOX.SETTINGS.TYPING.HIERARCHY.DISABLED'),
            })
          }}
        </p>
        <NextButton
          v-if="savingTyping"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- Group Management Section -->
    <SettingsSection
      v-if="serverInfo && !hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.GROUP_MANAGEMENT.LABEL')"
    >
      <div class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.GROUP_MANAGEMENT.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="groups-disabled"
              type="radio"
              name="groups"
              :checked="groupsEnabled === false"
              :disabled="savingGroups"
              @change="setGroups(false)"
            />
            <label for="groups-disabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.DISABLED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="groups-default"
              type="radio"
              name="groups"
              :checked="groupsEnabled === null"
              :disabled="savingGroups"
              @change="setGroups(null)"
            />
            <label for="groups-default" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.NOT_CONFIGURED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="groups-enabled"
              type="radio"
              name="groups"
              :checked="groupsEnabled === true"
              :disabled="savingGroups"
              @change="setGroups(true)"
            />
            <label for="groups-enabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.ENABLED') }}
            </label>
          </div>
        </div>
        <NextButton
          v-if="savingGroups"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- Calls Section -->
    <SettingsSection
      v-if="serverInfo && !hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.CALLS.LABEL')"
    >
      <div class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.CALLS.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="calls-disabled"
              type="radio"
              name="calls"
              :checked="callsEnabled === false"
              :disabled="savingCalls"
              @change="setCalls(false)"
            />
            <label for="calls-disabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.DISABLED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="calls-default"
              type="radio"
              name="calls"
              :checked="callsEnabled === null"
              :disabled="savingCalls"
              @change="setCalls(null)"
            />
            <label for="calls-default" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.NOT_CONFIGURED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="calls-enabled"
              type="radio"
              name="calls"
              :checked="callsEnabled === true"
              :disabled="savingCalls"
              @change="setCalls(true)"
            />
            <label for="calls-enabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.ENABLED') }}
            </label>
          </div>
        </div>
        <NextButton
          v-if="savingCalls"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- Broadcasts Section -->
    <SettingsSection
      v-if="serverInfo && !hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.BROADCASTS.LABEL')"
    >
      <div class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.BROADCASTS.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="broadcasts-disabled"
              type="radio"
              name="broadcasts"
              :checked="broadcastsEnabled === false"
              :disabled="savingBroadcasts"
              @change="setBroadcasts(false)"
            />
            <label for="broadcasts-disabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.DISABLED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="broadcasts-default"
              type="radio"
              name="broadcasts"
              :checked="broadcastsEnabled === null"
              :disabled="savingBroadcasts"
              @change="setBroadcasts(null)"
            />
            <label for="broadcasts-default" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.NOT_CONFIGURED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="broadcasts-enabled"
              type="radio"
              name="broadcasts"
              :checked="broadcastsEnabled === true"
              :disabled="savingBroadcasts"
              @change="setBroadcasts(true)"
            />
            <label for="broadcasts-enabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.ENABLED') }}
            </label>
          </div>
        </div>
        <NextButton
          v-if="savingBroadcasts"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- Read Receipts Section -->
    <SettingsSection
      v-if="serverInfo && !hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.READ_RECEIPTS.LABEL')"
    >
      <div class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.READ_RECEIPTS.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="readreceipts-disabled"
              type="radio"
              name="readreceipts"
              :checked="readreceiptsEnabled === false"
              :disabled="savingReadreceipts"
              @change="setReadreceipts(false)"
            />
            <label for="readreceipts-disabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.DISABLED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="readreceipts-default"
              type="radio"
              name="readreceipts"
              :checked="readreceiptsEnabled === null"
              :disabled="savingReadreceipts"
              @change="setReadreceipts(null)"
            />
            <label for="readreceipts-default" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.NOT_CONFIGURED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="readreceipts-enabled"
              type="radio"
              name="readreceipts"
              :checked="readreceiptsEnabled === true"
              :disabled="savingReadreceipts"
              @change="setReadreceipts(true)"
            />
            <label for="readreceipts-enabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.ENABLED') }}
            </label>
          </div>
        </div>
        <NextButton
          v-if="savingReadreceipts"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- Read Update Section -->
    <SettingsSection
      v-if="serverInfo && !hasBlockingServerError"
      :title="$t('QUEPASA_INBOX.SETTINGS.READ_UPDATE.LABEL')"
    >
      <div class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.READ_UPDATE.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="readupdate-disabled"
              type="radio"
              name="readupdate"
              :checked="readUpdateEnabled === false"
              :disabled="savingReadUpdate"
              @change="setReadUpdate(false)"
            />
            <label for="readupdate-disabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.DISABLED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="readupdate-default"
              type="radio"
              name="readupdate"
              :checked="readUpdateEnabled === null"
              :disabled="savingReadUpdate"
              @change="setReadUpdate(null)"
            />
            <label for="readupdate-default" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.NOT_CONFIGURED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="readupdate-enabled"
              type="radio"
              name="readupdate"
              :checked="readUpdateEnabled === true"
              :disabled="savingReadUpdate"
              @change="setReadUpdate(true)"
            />
            <label for="readupdate-enabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.STATES.ENABLED') }}
            </label>
          </div>
        </div>
        <NextButton
          v-if="savingReadUpdate"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          is-loading
        />
      </div>
    </SettingsSection>
    <!-- Open Conversations on State Change (Tri-state) -->
    <SettingsSection :title="$t('QUEPASA_INBOX.SETTINGS.NOTIFY_STATE.LABEL')">
      <div class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('QUEPASA_INBOX.SETTINGS.NOTIFY_STATE.HELP_TEXT') }}
        </p>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="notify-state-disabled"
              type="radio"
              name="notify-state"
              :checked="notifyState === false"
              @change="
                notifyState = false;
                saveNotifyState();
              "
            />
            <label for="notify-state-disabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.NOTIFY_STATE.DISABLED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="notify-state-default"
              type="radio"
              name="notify-state"
              :checked="notifyState === null"
              @change="
                notifyState = null;
                saveNotifyState();
              "
            />
            <label for="notify-state-default" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.NOTIFY_STATE.NOT_CONFIGURED') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              id="notify-state-enabled"
              type="radio"
              name="notify-state"
              :checked="notifyState === true"
              @change="
                notifyState = true;
                saveNotifyState();
              "
            />
            <label for="notify-state-enabled" class="text-sm text-n-slate-12">
              {{ $t('QUEPASA_INBOX.SETTINGS.NOTIFY_STATE.ENABLED') }}
            </label>
          </div>
        </div>
      </div>
    </SettingsSection>

    <!-- Integration mode: legacy (n8n) -> direct QuePasa Channel migration -->
    <SettingsSection
      title="Modo de integração"
      sub-title="Migre esta caixa para o QuePasa Channel direto (sem n8n). A caixa de entrada, conversas e histórico são mantidos; é possível voltar ao modo legado depois."
    >
      <NextButton
        class="self-start"
        color="teal"
        variant="outline"
        data-target="quepasa-inbox-migrate-to-channel"
        icon="i-lucide-arrow-left-right"
        label="Migrar para QuePasa Channel (direto)"
        :is-loading="isMigratingToChannel"
        @click="migrateToChannel"
      />
    </SettingsSection>
  </div>
</template>

<style scoped>
/* Toggle Switch Styles */
.peer:checked ~ div {
  background-color: rgb(var(--color-woot-500));
}

.peer:checked ~ div::after {
  transform: translateX(100%);
}
</style>
